import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SpaceKind, SpaceNodeKind } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import {
  FILE_INVALIDATION_KEYS,
  FOLDER_INVALIDATION_KEYS,
  NODE_ROUTE_SEGMENT,
  SPACE_BACKEND_ROUTE,
  VAULT_FILE_INVALIDATION_KEYS,
} from '../constants/spaces-query.const';
import { SidebarTreeStore } from '../store/sidebar-tree.store';
import { SpaceRef } from '../types/data-room.types';
import { invalidateSpaceFolderLevels } from './use-space-navigation-folders.hook';

const NOT_FOUND_STATUS = 404;

type DeleteNodeVariables = {
  id: string;
  kind: SpaceNodeKind;
  space: SpaceRef;
};

// A tree space deletes through its own routes; the Vault, whose files live apart, through its own.
const deleteRoute = (companyId: string, { id, kind, space }: DeleteNodeVariables) =>
  space.kind === SpaceKind.VAULT
    ? {
        endpoint: BackendRoute.COMPANIES,
        routePath: [companyId, 'vault', NODE_ROUTE_SEGMENT[kind], id],
      }
    : {
        endpoint: SPACE_BACKEND_ROUTE[space.kind],
        routePath: [space.id, NODE_ROUTE_SEGMENT[kind], id],
      };

type Props = {
  onSuccess?: () => void;
};

export const useDeleteSpaceNode = ({ onSuccess }: Props = {}) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  // A folder also leaves its space's sidebar levels; a file only its folder's contents and counts.
  const { invalidateQuery: invalidateAfterFile } = useInvalidateQueries(...FILE_INVALIDATION_KEYS);
  const { invalidateQuery: invalidateAfterFolder } = useInvalidateQueries(
    ...FOLDER_INVALIDATION_KEYS,
  );
  const { invalidateQuery: invalidateAfterVaultFile } = useInvalidateQueries(
    ...VAULT_FILE_INVALIDATION_KEYS,
  );

  const { isPending, mutate: deleteNode } = useAppMutation<
    void,
    HttpException,
    DeleteNodeVariables
  >([QueryKey.DELETE_SPACE_NODE, companyId], {
    mutationFn: (variables) => ApiService.delete(deleteRoute(companyId, variables)),
    // Already gone, deleted elsewhere: the stale row has to leave the table. The default handling
    // still toasts the failure.
    onError: (error, { kind, space }) => {
      if (error.response?.status !== NOT_FOUND_STATUS) return;

      if (space.kind === SpaceKind.VAULT) {
        invalidateAfterVaultFile();
      } else if (kind === SpaceNodeKind.FILE) {
        invalidateAfterFile();
      } else {
        invalidateAfterFolder();
        invalidateSpaceFolderLevels(queryClient, companyId, space);
      }
    },
    onSuccess: (_, { id, kind, space }) => {
      if (space.kind === SpaceKind.VAULT) {
        toast.success(messagesLocale['FILE_DELETED_SUCCESS']);
        invalidateAfterVaultFile();
      } else if (kind === SpaceNodeKind.FILE) {
        toast.success(messagesLocale['FILE_DELETED_SUCCESS']);
        invalidateAfterFile();
      } else {
        toast.success(messagesLocale['FOLDER_DELETED_SUCCESS']);
        invalidateAfterFolder();
        invalidateSpaceFolderLevels(queryClient, companyId, space);
        SidebarTreeStore.getState().leaveDeletedFolder(space.id, id);
      }

      onSuccess?.();
    },
  });

  return { deleteNode, isPending };
};
